<template>
  <div>
    <div v-if="loadingAccount" class="flex justify-center">
      <span class="loading mt-24 loading-lg loading-dots text-gray-500" />
    </div>

    <div v-else>
      <div id="parent-account-header" class="inner">
        <div class="flex place-content-between items-center gap-5">
          <div>
            <div class="mb-1 flex items-center gap-3">
              <h1 class="mb-1 max-w-lg truncate" data-test="account-name">{{ account.displayName }}</h1>
              <div
                v-if="IsUserAnAdmin(Store.state.user) || IsUserABroker(Store.state.user)"
                class="tooltip tooltip-bottom flex items-center"
                data-tip="View as Client">
                <ViewIcon
                  :size="16"
                  class="cursor-pointer stroke-capital-blue"
                  @click="handleViewAsClient(account.accountID)" />
              </div>
              <SalesforceLink v-if="!isAClientAccount" :sales-force-id="account.salesForceID" />
            </div>
            <a
              v-if="showDomain && !isAClientAccount"
              :href="`//${account.domainName}`"
              target="_blank"
              rel="noopener noreferrer"
              class="lowercase"
              >{{ account.domainName }}</a
            >
            <span v-if="showDomain && !isAClientAccount" class="text-md text-gray-500"> &nbsp; </span>
            <span
              v-if="!isAClientAccount"
              class="text-xs text-gray-500 uppercase"
              data-test="account-industry">
              {{ account.topLevelIndustry }}</span
            >
          </div>

          <div
            class="mx-auto mt-3 alert w-1/2 alert-error"
            v-visible="!loadingKPI && errorKPI">
            <AlertCircle />
            <span>{{ errorKPI }}</span>
          </div>
          <div class="text-right statistics">
            <Skeleton v-if="loadingKPI && !errorKPI" shape="rectangle" width="100%" height="80px" class="rounded-md" />
            <Statistics
              v-if="!loadingKPI && !errorKPI"
              dense
              no-borders
              :statistics="statistics"
              data-test="parent-kpi" />
          </div>
        </div>
      </div>

      <div class="w-full">
        <Tabs
          id="parent-account-tabs"
          :model-value="currentTab"
          :items="accountTabItems"
          class="tab-bar uppercase w-full"
          variant="lifted"
          @update:model-value="onAccountTabChange" />
      </div>

      <div
        v-if="!loadingTabs && accountTabs.length === 0"
        class="alert p-4 w-xl mx-auto mt-8"
      >
        <TriangleAlert size="18"/>
        <span>
        Insufficient permissions. Contact <a href="mailto:platform@cacgroup.com">platform@cacgroup.com</a>
        for assistance.
      </span>
      </div>

      <router-view v-if="!loadingAccount && accountTabs.length > 0" :account="account"></router-view>
    </div>
  </div>
</template>

<script>
import { accountService } from '@/services/api';
import Statistics from '@/components/Statistics.vue';
import { DateTime } from 'luxon';
import { compactMoney } from '@/utilities/Money';
import { compactNumber } from '@/utilities/Number';
import { ErrorLoadingKPI } from '@/utilities/Errors';
import { getElementWidth, getElementHeight } from '@/utilities/DOM';
import { Commit, Store } from '@/store';
import { Events } from '@/store/EventBus';
import { StoreMutationType } from '@/types/StoreMutationType';
import Skeleton from '@/components/v2/Skeleton.vue';
import Tabs from '@/components/v2/Tabs.vue';
import SalesforceLink from '@/components/SalesforceLink.vue';
import { AlertCircle, TriangleAlert, View as ViewIcon } from '@lucide/vue';
import { IsUserAClient, IsUserABroker, IsUserAnAdmin } from '@/utilities/AuthUtils';
import { registerResizeItem } from '@/utilities/resize';
import { useEntityTabs } from '@/composables/useEntityTabs.ts';

export default {
  name: 'ParentAccountView',
  components: { Skeleton, Tabs, TriangleAlert, AlertCircle, Statistics, ViewIcon, SalesforceLink },
  setup() {
    const { getTabs } = useEntityTabs();

    return {
      getTabs,
    };
  },
  data: () => ({
    loadingAccount: true,
    loadingKPI: true,
    loadingTabs: true,
    accountTabs: [],
    errorKPI: undefined,
    account: {},
    statistics: [],
    includeExternal: false,
    showCurrentOnly: true,
    headerViewDimensions: { width: 0, height: 0 },
  }),
  computed: {
    showDomain() {
      if (this.account.noDomain === true) {
        return false;
      }
      return this.account.domainName;
    },
    isAClientAccount() {
      return IsUserAClient(Store.state.user);
    },
    Store() {
      return Store;
    },
    currentTab() {
      // Parent account path is /parent/account/:id/:tabKey (tab at index 4); account path is /account/:id/:tabKey (tab at index 3)
      return this.$route.path.split('/')[4];
    },
    accountTabItems() {
      return this.accountTabs.map((t) => ({ value: t.key, label: t.label }));
    },
  },
  methods: {
    onAccountTabChange(val) {
      const tab = this.accountTabs.find((t) => t.key === val);
      if (tab) this.$router.push(tab.route);
    },
    IsUserAnAdmin,
    IsUserABroker,
    async loadAccount() {
      this.loadingAccount = true;
      this.account = await accountService.findById(this.$route.params.id);

      // fetch parent account KPIs with external policies for prospect accounts
      if (this.account.accountStatus === 'P') {
        this.includeExternal = true;
      }

      this.loadingAccount = false;
    },
    async refreshKPI() {
      try {
        this.loadingKPI = true;
        const kpi = await accountService.kpiByParentId(this.$route.params.id, this.includeExternal, this.showCurrentOnly);
        let memberSince = DateTime.fromISO(kpi.memberSince).toLocaleString(DateTime.DATE_SHORT);
        if (memberSince.includes('Invalid')) {
          memberSince = '--';
        }
        this.statistics = [
          {
            key: 'member-since',
            value: memberSince,
            heading: this.includeExternal === true ? 'Target Since' : 'Member Since',
            loading: false,
            classes: ['dense', 'd-lg-block'],
          },
          {
            key: 'company-count',
            value: compactNumber(kpi.totalCompanyCount),
            heading: this.includeExternal === true ? 'Total Companies' : `Current Clients`,
            loading: false,
            classes: ['dense'],
          },
          {
            key: 'total-premium',
            value: compactMoney(kpi.totalPremium),
            heading: this.includeExternal === true ? 'Total Premium' : `Current Premium`,
            loading: false,
            classes: ['dense'],
            toolTip: 'Includes BOR Premium',
          },
          {
            key: 'policy-count',
            value: compactNumber(kpi.totalPolicyCount),
            heading: this.includeExternal === true ? 'Total Policies' : `Current Policies`,
            loading: false,
            classes: ['dense'],
          },
        ];
      } catch (err) {
        console.error(err);
        this.errorKPI = ErrorLoadingKPI;
      } finally {
        this.loadingKPI = false;
      }
    },
    async handleViewAsClient(accountID) {
      Commit(StoreMutationType.ENABLE_CLIENT_VIEW_MODE, {
        id: -1,
        name: '',
        allowedAccountIds: [accountID],
      });

      await this.loadAccount();
      await this.refreshKPI();
    },
    async loadParentAccountTabs() {
      this.loadingTabs = true;
      try {
        this.accountTabs = await this.getTabs('parent-account', this.account.accountID.toString());
      } catch (error) {
        console.error('Error loading parent account tabs:', error);
        this.accountTabs = [];
      } finally {
        this.loadingTabs = false;
      }
    },
  },
  watch: {
    async isAClientAccount() {
      // when client view is exited, fetch KPIs and display
      await this.loadAccount();
      await this.refreshKPI();
      await this.loadParentAccountTabs();
    },
    accountTabs() {
      if (!this.accountTabs.map((t) => t.key).includes(this.currentTab)) {
        if (this.accountTabs.length > 0) {
          this.$router.push(this.accountTabs[0].route);
        }
      }
    }
  },

  // -----------
  // lifecycles
  // -----------
  async created() {
    await this.loadAccount();
    await this.refreshKPI();
    await this.loadParentAccountTabs();
    addEventListener(Events.REFRESH_ACCOUNT_KPI, this.refreshKPI);
  },
  mounted() {
    registerResizeItem({ name: 'parent-account-header', selector: 'parent-account-header' });
    registerResizeItem({ name: 'parent-account-tabs', selector: 'parent-account-tabs' });
  },
  beforeUnmount() {
    removeEventListener(Events.REFRESH_ACCOUNT_KPI, this.refreshKPI);
  },
  updated() {
    const parentAccountHeader = document.getElementById('parent-account-header');
    const parentAccountTabs = document.getElementById('parent-account-tabs');
    this.headerViewDimensions.width = getElementWidth(parentAccountHeader) + getElementWidth(parentAccountTabs);
    this.headerViewDimensions.height = getElementHeight(parentAccountHeader) + getElementHeight(parentAccountTabs);
  },
};
</script>
